import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

import AllianceLogo from "../assets/Alliance_logo.webp";
import CGIARLogo from "../assets/CGIAR_logo.webp";
import SDSNLogo from "../assets/SDSN_logo.webp";
import iiasaIcon from "../assets/iiasa.png";
import scenathonLogo from "../assets/scenathon_logo.png";
import WorldMap from "../assets/ethiopiaMap.png";
import IconFoodSupply from "../assets/icon-food-supply.png";
import IconForest from "../assets/icon-forest.png";
import IconCropland from "../assets/icon-cropland.png";
import DietIcon from "../assets/icon-secure-nutritious-diets.png";
import LandIcon from "../assets/icon-use-land-sustainably.png";
import ClimateIcon from "../assets/icon-cut-emissions-protect-biodiversity.png";
/* -------------------------------------------------------
   SMALL ICONS
------------------------------------------------------- */

const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-4 w-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 12h14M13 6l6 6-6 6"
    />
  </svg>
);

const CompassIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-5 w-5"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="m15.5 8.5-2.2 4.8-4.8 2.2 2.2-4.8 4.8-2.2Z" />
  </svg>
);

const BookIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-5 w-5"
  >
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H11v16H6.5A2.5 2.5 0 0 0 4 21.5v-16Z" />
    <path d="M20 5.5A2.5 2.5 0 0 0 17.5 3H13v16h4.5a2.5 2.5 0 0 1 2.5 2.5v-16Z" />
  </svg>
);

function getUserInitials(user) {
  if (!user) return "";
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  if (!name) return "";
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "";
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

const DashboardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    className="h-4 w-4"
  >
    <rect x="4" y="4" width="6" height="6" rx="1" />
    <rect x="14" y="4" width="6" height="6" rx="1" />
    <rect x="4" y="14" width="6" height="6" rx="1" />
    <rect x="14" y="14" width="6" height="6" rx="1" />
  </svg>
);

/* -------------------------------------------------------
   METRIC CARD
------------------------------------------------------- */

function MetricCard({
  icon,
  iconSrc,
  label,
  value,
  unit,
  iconColor = "text-emerald-700",
}) {
  return (
    <div
      className="
      flex min-h-[76px] items-center gap-3
      rounded-lg border border-slate-200/90
      bg-white/95 px-4 py-3
      shadow-[0_3px_12px_rgba(15,23,42,0.08)]
      backdrop-blur
    "
    >
      <div
        className={`
          flex h-11 w-11 shrink-0 items-center justify-center
          ${iconColor}
        `}
      >
        {iconSrc ? (
          <img src={iconSrc} alt={label} className="h-10 w-10 object-contain" />
        ) : (
          <span className="text-3xl">{icon}</span>
        )}
      </div>

      <div className="border-l border-slate-200 pl-3">
        <p className="text-[12px] font-semibold leading-none text-slate-700">
          {label}
        </p>

        <p className="mt-1 text-[23px] font-extrabold leading-none tracking-tight text-slate-900">
          {value}
        </p>

        <p className="mt-1 text-[10px] text-slate-500">{unit}</p>
      </div>
    </div>
  );
}

/* -------------------------------------------------------
   POLICY CARD
------------------------------------------------------- */

function PolicyCard({ icon, iconSrc, title, description, theme = "green", onClick }) {
  const themes = {
    green: {
      circle: "bg-[#f5f1d8]",
      icon: "text-[#08725b]",
    },
    land: {
      circle: "bg-[#eef5df]",
      icon: "text-[#478c35]",
    },
    blue: {
      circle: "bg-[#e7f2fb]",
      icon: "text-[#14739b]",
    },
  };

  const current = themes[theme];

  return (
    <button
      type="button"
      onClick={onClick}
      className="
        group flex w-full min-h-[94px] items-center gap-5
        rounded-md border border-slate-200
        bg-white/75 px-5 py-4 text-left
        transition-all duration-200
        hover:-translate-y-[2px]
        hover:bg-white
        hover:shadow-md
      "
    >
      {/* ICON */}
      <div
        className={`
          flex h-14 w-14 shrink-0 items-center justify-center
          rounded-full
          ${current.circle} ${current.icon}
        `}
      >
        {iconSrc ? (
          <img src={iconSrc} alt={title} className="h-10 w-10 object-contain" />
        ) : (
          <span className="text-3xl">{icon}</span>
        )}
      </div>

      {/* TEXT */}
      <div className="min-w-0 flex-1">
        <h3 className="text-[14px] font-bold leading-tight text-slate-900">
          {title}
        </h3>

        <p className="mt-2 max-w-[250px] text-[11px] leading-[1.45] text-slate-600">
          {description}
        </p>
      </div>

      {/* ARROW */}
      <div
        className="
          flex h-8 w-8 shrink-0 items-center justify-center
          rounded-full border border-slate-400
          text-slate-700
          transition
          group-hover:border-emerald-700
          group-hover:text-emerald-700
        "
      >
        <ArrowIcon />
      </div>
    </button>
  );
}

/* =======================================================
   MAIN COMPONENT
======================================================= */

export default function EthiopiaLanding() {
  const { user } = useAuth();
  const { openLoginModal } = useOutletContext();
  const navigate = useNavigate();
  const userInitials = getUserInitials(user);
  const [currentTrendsFoodSupply, setCurrentTrendsFoodSupply] = useState(null);
  const [currentTrendsForest, setCurrentTrendsForest] = useState(null);
  const [currentTrendsCropland, setCurrentTrendsCropland] = useState(null);

  const handlePolicyCardClick = (targetTab) => {
    if (user) {
      navigate(`/scenarios?tab=${targetTab}`);
    } else {
      openLoginModal();
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    async function loadCurrentTrendsFoodSupply() {
      try {
        const pathwaysRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/public/pathways`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
          },
        );

        if (!pathwaysRes.ok) {
          throw new Error(`Pathways fetch failed: ${pathwaysRes.status}`);
        }

        const pathwaysData = await pathwaysRes.json();
        const pathways = Array.isArray(pathwaysData.pathways)
          ? pathwaysData.pathways
          : [];

        const currentTrendsPathway = pathways.find((pathway) => {
          const name = String(pathway.name || "").toLowerCase();
          return name.includes("current") && name.includes("trend");
        });

        if (!currentTrendsPathway) {
          setCurrentTrendsFoodSupply(null);
          return;
        }

        const params = new URLSearchParams({
          pathwayId: String(currentTrendsPathway.id),
        });
        const resultsRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/public/results?${params.toString()}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            signal: controller.signal,
          },
        );

        if (!resultsRes.ok) {
          throw new Error(`Results fetch failed: ${resultsRes.status}`);
        }

        const resultsData = await resultsRes.json();
        const resultsObj = Array.isArray(resultsData)
          ? (resultsData[0] ?? null)
          : resultsData;
        const pathwayEntry = resultsObj ? Object.values(resultsObj)[0] : null;
        const intakeData =
          pathwayEntry?.charts?.[0]?.Food?.["Daily per capita food intake"] ??
          [];
        const row2050 = intakeData.find((row) => Number(row.year) === 2050);

        if (row2050 && typeof row2050.feasibleKcal === "number") {
          setCurrentTrendsFoodSupply(Math.round(row2050.feasibleKcal));
        } else {
          setCurrentTrendsFoodSupply(null);
        }

        const areaData =
          pathwayEntry?.charts?.[0]?.["Land use"]?.["Area by landcover"] ?? [];
        const areaRow2050 = areaData.find((row) => Number(row.year) === 2050);
        const forestValue = Number(areaRow2050?.forest);
        const croplandValue = Number(areaRow2050?.cropland);

        if (areaRow2050 && Number.isFinite(forestValue)) {
          setCurrentTrendsForest(Number(forestValue.toFixed(1)));
        } else {
          setCurrentTrendsForest(null);
        }

        if (areaRow2050 && Number.isFinite(croplandValue)) {
          setCurrentTrendsCropland(Number(croplandValue.toFixed(1)));
        } else {
          setCurrentTrendsCropland(null);
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error loading Current Trends food supply:", error);
        setCurrentTrendsFoodSupply(null);
      }
    }

    loadCurrentTrendsFoodSupply();
    return () => controller.abort();
  }, []);

  const foodSupplyValue =
    currentTrendsFoodSupply !== null ? currentTrendsFoodSupply : "";
  const forestAreaValueMha =
    currentTrendsForest !== null
      ? (currentTrendsForest / 1000).toFixed(2)
      : "";
  const croplandAreaValueMha =
    currentTrendsCropland !== null
      ? (currentTrendsCropland / 1000).toFixed(2)
      : "";

  return (
    <div className="(min-h-screen-64px) bg-white text-slate-900">
      {/* =================================================
          HERO
      ================================================= */}

      <main>
        <section
          className="
            relative overflow-hidden
            border-b border-slate-200
            bg-[#fafaf0]
          "
        >
          {/* Background gradient */}
          <div
            className="
              pointer-events-none absolute inset-0
              bg-[radial-gradient(circle_at_66%_25%,rgba(187,219,170,0.28),transparent_37%),radial-gradient(circle_at_10%_60%,rgba(255,246,193,0.35),transparent_35%)]
            "
          />

          <div
            className="
              relative mx-auto
              grid min-h-[550px]
              w-full max-w-[1600px]
              grid-cols-1
              items-center
              gap-5
              px-6 py-8
              md:px-10
              xl:grid-cols-[38%_44%_18%]
              xl:px-14
            "
          >
            {/* -----------------------------------------
                LEFT COPY
            ----------------------------------------- */}

            <div className="relative z-10 max-w-[580px] py-3">
              <h1
                className="
                  max-w-[560px]
                  text-[37px]
                  font-black
                  leading-[1.12]
                  tracking-[-0.035em]
                  text-[#07152d]
                  sm:text-[42px]
                  xl:text-[45px]
                "
              >
                Plan Ethiopia’s food,
                <br />
                land and climate future
              </h1>

              <p
                className="
                  mt-4 max-w-[520px]
                  text-[16px]
                  font-medium
                  leading-[1.45]
                  text-slate-700
                "
              >
                Explore FABLE pathways to compare trade-offs and
                <br className="hidden sm:block" />
                identify policy options through 2050.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                {user ? (
                  <Link
                    to="/pathways"
                    className="
                      inline-flex h-[47px] items-center gap-2
                      rounded-md
                      bg-[#00664e]
                      px-6
                      text-[13px] font-semibold text-white
                      shadow-sm
                      transition
                      hover:bg-[#005441]
                    "
                  >
                    <CompassIcon />
                    Explore pathways
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={openLoginModal}
                    className="
                      inline-flex h-[47px] items-center gap-2
                      rounded-md
                      bg-[#00664e]
                      px-6
                      text-[13px] font-semibold text-white
                      shadow-sm
                      transition
                      hover:bg-[#005441]
                    "
                  >
                    <CompassIcon />
                    Explore pathways
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (user) {
                      navigate("/pathways", { state: { openTutorial: true } });
                    } else {
                      openLoginModal();
                    }
                  }}
                  className="
                    inline-flex h-[47px] items-center gap-2
                    rounded-md
                    border border-[#5b8c83]
                    bg-white/60
                    px-6
                    text-[13px] font-semibold text-[#174f46]
                    transition
                    hover:bg-white
                  "
                >
                  <BookIcon />
                  How to use this dashboard
                </button>
              </div>
            </div>

            {/* -----------------------------------------
                MAP
            ----------------------------------------- */}

            <div
              className="
                relative
                flex h-[380px]
                items-center justify-center
                md:h-[420px]
                xl:h-[440px]
              "
            >
              <img
                src={WorldMap}
                alt="Land cover map of Ethiopia"
                className="
                  relative z-[2]
                  h-full max-h-[420px]
                  w-full
                  scale-[1.2]
                  object-contain
                  drop-shadow-[0_10px_9px_rgba(28,63,39,0.18)]
                  translate-x-8
                  xl:translate-x-24
                "
              />
            </div>

            {/* -----------------------------------------
                METRICS RIGHT
            ----------------------------------------- */}

            <aside className="relative z-10 w-full xl:max-w-[205px]">
              <p
                className="
                mb-3
                text-[11px]
                text-center
                font-bold
                uppercase
                tracking-[0.2em]
                text-[#00664e]
              "
              >
                Current trends · 2050
              </p>

              <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                <MetricCard
                  iconSrc={IconFoodSupply}
                  label="Food supply"
                  value={foodSupplyValue}
                  unit="kcal/cap/day"
                  iconColor="text-amber-500"
                />

                <MetricCard
                  iconSrc={IconForest}
                  label="Forest"
                  value={forestAreaValueMha}
                  unit="Mha"
                  iconColor="text-[#006a50]"
                />

                <MetricCard
                  iconSrc={IconCropland}
                  label="Cropland"
                  value={croplandAreaValueMha}
                  unit="Mha"
                  iconColor="text-amber-500"
                />
              </div>
            </aside>

            {/* -----------------------------------------
                LAND COVER LEGEND
            ----------------------------------------- */}

            <div
              className="
                col-span-full
                -mt-4
                flex flex-wrap
                items-center justify-center
                gap-x-5 gap-y-2
                xl:absolute
                xl:bottom-[8px]
                xl:left-[68%]
                xl:-translate-x-1/2
              "
            >
              {[
                ["#ffbf16", "Cultivated"],
                ["#006550", "Dense Forest"],
                ["#92bc66", "Savanna"],
                ["#927548", "Bare Soil"],
                ["#fa7253", "Urban/Built"],
                ["#1675b9", "Water/Wetland"],
              ].map(([color, name]) => (
                <div
                  key={name}
                  className="flex items-center gap-2 text-[9px] font-medium text-slate-700"
                >
                  <span
                    className="h-[10px] w-[10px] rounded-[1px]"
                    style={{ backgroundColor: color }}
                  />
                  {name}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =================================================
            POLICY QUESTIONS
        ================================================= */}

        <section className="bg-white">
          <div className="mx-auto w-full max-w-[1600px] px-6 py-6 md:px-10 xl:px-14">
            <h2
              className="
                mb-4
                text-[19px]
                font-extrabold
                tracking-[-0.02em]
                text-[#07152d]
              "
            >
              Start with a policy question
            </h2>

            <div className="grid gap-8 md:grid-cols-3">
              <PolicyCard
                iconSrc={DietIcon}
                theme="green"
                title="Secure nutritious diets"
                description="Improve food availability, access, and nutrition for all Ethiopians."
                onClick={() => handlePolicyCardClick(1)}
              />

              <PolicyCard
                iconSrc={LandIcon}
                theme="land"
                title="Use land sustainably"
                description="Balance food production with soil health, forests, and water resources."
                onClick={() => handlePolicyCardClick(5)}
              />

              <PolicyCard
                iconSrc={ClimateIcon}
                theme="blue"
                title="Cut emissions & protect biodiversity"
                description="Reduce emissions while conserving nature and ecosystem services."
                onClick={() => handlePolicyCardClick(4)}
              />
            </div>
          </div>
        </section>

        {/* =================================================
            PARTNERS
        ================================================= */}

        <footer className="border-t border-slate-100 bg-white mt-8">
          <div
            className="
              mx-auto flex min-h-[65px]
              w-full max-w-[1600px]
              flex-wrap items-center justify-center
              gap-y-5
              px-8 py-3
            "
          >
            <a
              href="https://www.alliancebioversityciat.org"
              target="_blank"
              rel="noreferrer"
              className="
                flex min-w-[150px] items-center justify-center
                border-r border-slate-200 px-8
              "
            >
              <img
                src={AllianceLogo}
                alt="Alliance Bioversity & CIAT"
                className="max-h-[52px] max-w-[140px] object-contain"
              />
            </a>

            <a
              href="https://www.cgiar.org"
              target="_blank"
              rel="noreferrer"
              className="
                flex min-w-[150px] items-center justify-center
                border-r border-slate-200 px-8
              "
            >
              <img
                src={CGIARLogo}
                alt="CGIAR"
                className="max-h-[52px] max-w-[140px] object-contain"
              />
            </a>

            <a
              href="https://www.unsdsn.org"
              target="_blank"
              rel="noreferrer"
              className="
                flex min-w-[150px] items-center justify-center
                border-r border-slate-200 px-8
              "
            >
              <img
                src={SDSNLogo}
                alt="SDSN"
                className="max-h-[52px] max-w-[130px] object-contain"
              />
            </a>

            <a
              href="https://www.iiasa.ac.at"
              target="_blank"
              rel="noreferrer"
              className="
                flex min-w-[150px] items-center justify-center px-8
                border-r border-slate-200
              "
            >
              <img
                src={iiasaIcon}
                alt="IIASA"
                className="max-h-[52px] max-w-[125px] object-contain"
              />
            </a>

            <a
              href="https://www.lab.scenathon.org/"
              target="_blank"
              rel="noreferrer"
              className="
                flex min-w-[150px] items-center justify-center px-8
              "
            >
              <img
                src={scenathonLogo}
                alt="Scenathon"
                className="max-h-[52px] max-w-[140px] object-contain"
              />
            </a>
          </div>
        </footer>
      </main>
    </div>
  );
}
